import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { 
  BillingSettings, 
  InvoiceRequest, 
  InvoiceResponse,
  ProformaRequest,
  ProformaResponse,
  CreditNoteRequest,
  CreditNoteResponse,
  PdfDownloadRequest,
  PdfDownloadResponse,
  InvoiceListRequest,
  InvoiceListResponse,
  InvoiceStatistics
} from '../types/billing';

class BillingService {
  private collectionName = 'billingSettings';

  // Firebase Functions endpoints
  private createInvoiceFunction = httpsCallable<{invoiceData: InvoiceRequest, organizationId: string}, InvoiceResponse>(functions, 'createInvoice');
  private createProformaFunction = httpsCallable<{proformaData: ProformaRequest, organizationId: string}, ProformaResponse>(functions, 'createProforma');
  private createCreditNoteFunction = httpsCallable<{creditNoteData: CreditNoteRequest, organizationId: string}, CreditNoteResponse>(functions, 'createCreditNote');
  private downloadInvoicePdfFunction = httpsCallable<PdfDownloadRequest, PdfDownloadResponse>(functions, 'downloadInvoicePdf');
  private validateAgentKeyFunction = httpsCallable<{agentKey: string}, {valid: boolean, message: string}>(functions, 'validateAgentKey');
  private getInvoiceFunction = httpsCallable<{invoiceId: string, organizationId: string}, any>(functions, 'getInvoice');

  private getInvoiceStatisticsFunction = httpsCallable<{organizationId: string, period?: string}, {success: boolean, statistics: InvoiceStatistics}>(functions, 'getInvoiceStatistics');
  private updateInvoiceStatusFunction = httpsCallable<{invoiceId: string, organizationId: string, status: string, paymentDate?: string}, {success: boolean, message: string}>(functions, 'updateInvoiceStatus');


  // Számlázási beállítások lekérése szervezet szerint
  async getBillingSettings(organizationId: string): Promise<BillingSettings | null> {
    try {
      const q = query(
        collection(db, this.collectionName), 
        where('organizationId', '==', organizationId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
      } as BillingSettings;
    } catch (error) {
      console.error('Error fetching billing settings:', error);
      throw error;
    }
  }

  // Számlázási beállítások létrehozása
  async createBillingSettings(settings: Omit<BillingSettings, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...settings,
        createdAt: now,
        updatedAt: now
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating billing settings:', error);
      throw error;
    }
  }

  // Számlázási beállítások frissítése
  async updateBillingSettings(id: string, settings: Partial<BillingSettings>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...settings,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating billing settings:', error);
      throw error;
    }
  }

  // Számlázási beállítások törlése
  async deleteBillingSettings(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.collectionName, id));
    } catch (error) {
      console.error('Error deleting billing settings:', error);
      throw error;
    }
  }

  // Agent kulcs validálása (Firebase Function használatával)
  async validateAgentKey(agentKey: string): Promise<{valid: boolean, message: string}> {
    try {
      const result = await this.validateAgentKeyFunction({ agentKey });
      return result.data;
    } catch (error) {
      console.error('Error validating agent key:', error);
      return {
        valid: false,
        message: `Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // Számla kiállítása (Firebase Function használatával)
  async createInvoice(invoiceData: InvoiceRequest, organizationId: string): Promise<InvoiceResponse> {
    try {
      const result = await this.createInvoiceFunction({ invoiceData, organizationId });
      return result.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw new Error(`Failed to create invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Díjbekérő (proforma) kiállítása
  async createProforma(proformaData: ProformaRequest, organizationId: string): Promise<ProformaResponse> {
    try {
      const result = await this.createProformaFunction({ proformaData, organizationId });
      return result.data;
    } catch (error) {
      console.error('Error creating proforma:', error);
      throw new Error(`Failed to create proforma: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Sztornó számla (jóváírás) kiállítása
  async createCreditNote(creditNoteData: CreditNoteRequest, organizationId: string): Promise<CreditNoteResponse> {
    try {
      const result = await this.createCreditNoteFunction({ creditNoteData, organizationId });
      return result.data;
    } catch (error) {
      console.error('Error creating credit note:', error);
      throw new Error(`Failed to create credit note: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // PDF letöltése
  async downloadInvoicePdf(invoiceNumber: string, organizationId: string, invoiceType?: 'INVOICE' | 'PROFORMA' | 'CREDIT_NOTE'): Promise<PdfDownloadResponse> {
    try {
      const result = await this.downloadInvoicePdfFunction({ 
        invoiceNumber, 
        organizationId, 
        invoiceType 
      });
      return result.data;
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw new Error(`Failed to download PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Számla lekérése ID alapján
  async getInvoice(invoiceId: string, organizationId: string): Promise<any> {
    try {
      const result = await this.getInvoiceFunction({ invoiceId, organizationId });
      return result.data;
    } catch (error) {
      console.error('Error getting invoice:', error);
      throw error;
    }
  }



  // Statisztikák lekérése
  async getInvoiceStatistics(organizationId: string, period: string = 'month'): Promise<InvoiceStatistics> {
    try {
      const result = await this.getInvoiceStatisticsFunction({ organizationId, period });
      return result.data.statistics;
    } catch (error) {
      console.error('Error getting invoice statistics:', error);
      throw error;
    }
  }

  // Számla állapotának frissítése
  async updateInvoiceStatus(invoiceId: string, organizationId: string, status: 'PAID' | 'UNPAID' | 'OVERDUE' | 'CANCELLED', paymentDate?: Date): Promise<void> {
    try {
      const result = await this.updateInvoiceStatusFunction({ 
        invoiceId, 
        organizationId, 
        status,
        paymentDate: paymentDate?.toISOString()
      });
      
      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      throw error;
    }
  }

  // PDF letöltése és mentése
  async downloadAndSavePdf(invoiceNumber: string, organizationId: string, invoiceType?: 'INVOICE' | 'PROFORMA' | 'CREDIT_NOTE'): Promise<void> {
    try {
      const pdfResponse = await this.downloadInvoicePdf(invoiceNumber, organizationId, invoiceType);
      
      if (pdfResponse.success && pdfResponse.pdfBase64) {
        // Convert base64 to blob
        const byteCharacters = atob(pdfResponse.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdfResponse.fileName || `${invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('PDF download failed');
      }
    } catch (error) {
      console.error('Error downloading and saving PDF:', error);
      throw error;
    }
  }

  // Alapértelmezett beállítások lekérése
  getDefaultSettings(organizationId: string): Omit<BillingSettings, 'id' | 'createdAt' | 'updatedAt'> {
    return {
      organizationId,
      provider: 'szamlazz.hu',
      agentKey: '', // Üres - felhasználónak be kell állítania
      defaultPaymentMethod: 'CASH',
      defaultPaymentDeadline: 8,
      defaultCurrency: 'HUF',
      invoicePrefix: 'SZ-',
      szamlazzSettings: {
        invoiceLanguage: 'hu',
        responseVersion: 1,
        passiveSubmission: false,
        downloadPdf: true,
        downloadXml: false
      },
      isActive: true
    };
  }

  // Utility method to get payment method display name
  getPaymentMethodName(method: 'CASH' | 'CARD' | 'TRANSFER'): string {
    const names = {
      'CASH': 'Készpénz',
      'CARD': 'Bankkártya', 
      'TRANSFER': 'Átutalás'
    };
    return names[method] || method;
  }

  // Utility method to get invoice type display name
  getInvoiceTypeName(type: 'INVOICE' | 'PROFORMA' | 'CREDIT_NOTE'): string {
    const names = {
      'INVOICE': 'Számla',
      'PROFORMA': 'Díjbekérő',
      'CREDIT_NOTE': 'Jóváírás'
    };
    return names[type] || type;
  }

  // Utility method to get status display name and color
  getInvoiceStatusInfo(status: 'PAID' | 'UNPAID' | 'OVERDUE' | 'CANCELLED'): { name: string, color: string } {
    const statusInfo = {
      'PAID': { name: 'Kifizetve', color: 'green' },
      'UNPAID': { name: 'Kifizetetlen', color: 'orange' },
      'OVERDUE': { name: 'Lejárt', color: 'red' },
      'CANCELLED': { name: 'Sztornózva', color: 'gray' }
    };
    return statusInfo[status] || { name: status, color: 'gray' };
  }


}

export const billingService = new BillingService(); 